import LinkifyIt from 'linkify-it'

import { MarkTypeMap, NodeTypeMap } from '@showrunner/codex'

const linkify = new LinkifyIt()
const { BRACKET, CHARACTER, DIALOGUE, DOC, PAGE, PARENTHETICAL, TEXT } =
  NodeTypeMap

// TODO: handle dual dialogue (depth issues)
function getLastBlockType(state) {
  const { $anchor } = state.selection
  const blockIndex = $anchor.index(1)
  let lastBlock
  if (blockIndex < 1) {
    // check for previous page and get last node there
    const pageIndex = $anchor.index(0)
    // if there's no previous page, return null
    if (pageIndex < 1) {
      return null
    }
    // get previous page node
    const prevPage = $anchor.node(0).child(pageIndex - 1)
    // get last child of previous page
    lastBlock = prevPage.child(prevPage.childCount - 1)
  } else {
    // get block node at previous index
    lastBlock = $anchor.node(1).child(blockIndex - 1)
  }
  return lastBlock.type.name
}
/**
 * Determines if the given position is at the end of its parent node
 * @param {ResolvedPos} $pos - resolved position to check
 */
function isEndOfParentNode($pos) {
  return $pos.parentOffset === $pos.parent.content.size
}
/**
 * Determines if the given selection is a cursor positioned inside an empty block
 * @param {Selection} selection - PM Selection
 */
function isEmptyBlockSelection(selection) {
  return selection.empty && selection.$anchor.node().nodeSize === 2
}
/**
 * build up a single page JSON document by testing individual lines to see if they are
 * a recognizable Studio block type. assume dialogue otherwise
 *
 * @param {Array.<string>} lines - from a plaintext paste
 * @return {object} doc - PM JSON script
 */
function plainStudioToProse(lines) {
  const doc = { type: DOC, content: [{ type: PAGE }] }

  doc.content[0].content = lines.map((text) => {
    let content
    if (linkify.test(text)) {
      const intermediateDS = findLinksAndSplit(text)

      content = intermediateDS.map((row) => {
        // schema.nodeFromJSON() throws on blocks with a single empty string
        const result = row.text === '' ? null : { text: row.text, type: TEXT }

        if (result && row.url) {
          result.marks = [
            {
              type: MarkTypeMap.LINK,
              attrs: { href: row.url, autolink: true },
            },
          ]
        }
        return result
      })
    } else {
      // schema.nodeFromJSON() throws on blocks with a single empty string
      content = text === '' ? [] : [{ text, type: TEXT }]
    }

    // default to dialogue
    const block = { type: DIALOGUE, content }
    for (let i = 0; i < STUDIO_BLOCKS.length; i++) {
      if (text.match(STUDIO_REGEX[STUDIO_BLOCKS[i]])) {
        block.type = STUDIO_BLOCKS[i]
        break
      }
    }
    return block
  })
  return doc
}

/**
 * distinguish links within a paste payload and generate a data structure
 * that splits the original string at each and every valid link.
 *
 * findLinksAndSplit('something youtu.be/fe8 else')
 *
 * > [
 *   { text: 'something '},
 *   { text: 'youtu.be/fe8', url: 'http://youtu.be/fe8'},
 *   { text: ' else' }
 * ]
 *
 * @param {string} - a plaintext paste
 * @return {Array<{ text: string, url?: string}>} - Array of POJOs
 */
const findLinksAndSplit = (text) => {
  // linkify provides us with an array of links and their start/end positions
  // [{
  //   index: 10,
  //   lastIndex: 22,
  //   text: ''youtu.be/fe8',
  //   url: 'http://youtu.be/fe8'
  // }]
  const links = linkify.match(text)

  // be defensive
  if (!links || links.length < 1) return [{ text }]

  // [text, url?]
  let dataStore = []
  let start = 0
  let end = links[0].index

  const maybeAdd = (text, url) => {
    // dont inject empty text. (ie: a link began or terminated the entire string)
    if (start !== end && text !== '') {
      const row = { text }
      if (url) {
        row.url = url
      }
      dataStore.push(row)
    }
  }

  // reconstruct the entire original string by looping through each link
  // if intermediate plain text is present, make sure to include it
  links.forEach((l) => {
    end = l.index
    maybeAdd(text.slice(start, end))
    start = l.index
    end = l.lastIndex
    maybeAdd(l.text, l.url)
    start = l.lastIndex
  })
  // include the plain text that terminated the string (if present)
  end = text.length
  maybeAdd(text.slice(start, end))

  return dataStore
}
/*
  {***TREVOR***} is a Daily Show convention to get prompter specific formatting
  {SOT} and {VO} are an untimed Sam Bee stage direction convention
  note to john: when its time to add more, you'll either need to turn this
  into a plain old array or complicate the existing regexes for a block :)
*/
const STUDIO_REGEX = {
  [PARENTHETICAL]: /^(\(.+\)|\{(SOT|VO)})$/,
  [BRACKET]: /^(\[.+\])$/,
  [CHARACTER]: /^(\{\*\*\*.+\*\*\*\})$/,
}
const STUDIO_BLOCKS = Object.keys(STUDIO_REGEX)

export {
  getLastBlockType,
  findLinksAndSplit,
  isEndOfParentNode,
  isEmptyBlockSelection,
  plainStudioToProse,
  STUDIO_REGEX,
  STUDIO_BLOCKS,
}
